import { useState, useMemo, useRef } from 'react';
import { FileText, FileSpreadsheet, Printer, Share2, Save, FolderOpen } from 'lucide-react';
import LoanInputForm from './LoanInputForm';
import MonthlyCostSummary from './MonthlyCostSummary';
import AmortizationSchedule from './AmortizationSchedule';
import InterestRateComparison from './InterestRateComparison';
import LoanComparison from './LoanComparison';
import PrepaymentCalculator from './PrepaymentCalculator';
import { calculateMonthlyPayment, generateAmortizationSchedule, calculatePMI } from '../utils/mortgageCalculations';
import { calculatePropertyTax, getHomeownerInsurance, getStateFromZip } from '../utils/propertyDataService';
import { exportToPDF, exportToExcel, preparePrint } from '../utils/exportService';

function CalculatorPage({
    loanData,
    setLoanData,
    manualOverrides,
    setManualOverrides,
    comparisons,
    setComparisons,
    prepaymentData,
    setPrepaymentData,
    amortizationViewMode,
    setAmortizationViewMode,
    amortizationYear,
    setAmortizationYear,
    fileInputRef
}) {
    const [activeTab, setActiveTab] = useState('monthly');

    // Calculate derived values
    const calculatedData = useMemo(() => {
        const loanAmount = loanData.homePrice - loanData.downPayment;
        const monthlyPI = calculateMonthlyPayment(
            loanAmount,
            loanData.interestRate,
            loanData.loanYears
        );

        // Calculate auto-values (defaults)
        const autoPropertyTaxObj = calculatePropertyTax(loanData.homePrice, loanData.zipCode);
        const autoInsuranceObj = getHomeownerInsurance(loanData.zipCode, loanData.homePrice);

        const autoPropertyTax = autoPropertyTaxObj.monthly;
        const autoInsurance = autoInsuranceObj.monthly;
        const autoPMI = calculatePMI(loanAmount, loanData.homePrice, loanData.downPaymentPercent);

        // Calculate auto-percentage for property tax
        const autoPropertyTaxPercent = (autoPropertyTax * 12 / loanData.homePrice) * 100;

        const autoValues = {
            propertyTax: autoPropertyTax,
            propertyTaxPercent: autoPropertyTaxPercent,
            insurance: autoInsurance,
            pmi: autoPMI
        };

        // Use overrides if present, otherwise defaults
        const finalPropertyTax = manualOverrides.propertyTax !== null ? parseFloat(manualOverrides.propertyTax) : autoPropertyTax;
        const finalInsurance = manualOverrides.insurance !== null ? parseFloat(manualOverrides.insurance) : autoInsurance;
        const finalPMI = manualOverrides.pmi !== null ? parseFloat(manualOverrides.pmi) : autoPMI;

        // Calculate total escrow manually since we have the components
        const escrow = finalPropertyTax + finalInsurance + finalPMI;
        const totalMonthly = monthlyPI + escrow;

        // Get escrow details for display
        const escrowDetails = {
            propertyTaxDetails: {
                amount: finalPropertyTax,
                source: manualOverrides.propertyTax !== null ? 'Manual Override' : `Est. based on ${getStateFromZip(loanData.zipCode)}`
            },
            insuranceDetails: {
                amount: finalInsurance,
                source: manualOverrides.insurance !== null ? 'Manual Override' : `Est. based on ${getStateFromZip(loanData.zipCode)}`
            }
        };

        // Generate amortization schedule with PMI details
        const amortizationSchedule = generateAmortizationSchedule(
            loanAmount,
            loanData.interestRate,
            loanData.loanYears,
            loanData.loanType,
            loanData.homePrice,
            loanData.downPaymentPercent,
            finalPMI,  // Pass manual PMI override
            parseFloat(prepaymentData.extraPayment) || 0
        );

        return {
            loanAmount,
            monthlyPI,
            pmi: finalPMI,
            escrow,
            totalMonthly,
            escrowDetails,
            amortizationSchedule,
            autoValues
        };
    }, [loanData, manualOverrides, prepaymentData]);

    const handleExportPDF = () => {
        exportToPDF(loanData, calculatedData, prepaymentData, comparisons, manualOverrides);
    };

    const handleExportExcel = () => {
        exportToExcel(loanData, calculatedData, prepaymentData, comparisons, manualOverrides);
    };

    const handlePrint = () => {
        preparePrint();
    };

    const handleShare = () => {
        const config = {
            loanData,
            manualOverrides,
            comparisons,
            prepaymentData,
            amortizationViewMode,
            amortizationYear
        };

        try {
            const jsonString = JSON.stringify(config);
            const encoded = encodeURIComponent(btoa(jsonString));
            const url = `${window.location.origin}${window.location.pathname}?config=${encoded}`;

            navigator.clipboard.writeText(url).then(() => {
                alert('Shareable link copied to clipboard!');
            });
        } catch (error) {
            console.error('Error generating share link:', error);
            alert('Could not generate share link.');
        }
    };

    const handleSaveConfig = () => {
        const config = {
            version: '1.0',
            timestamp: new Date().toISOString(),
            data: {
                loanData,
                manualOverrides,
                comparisons,
                prepaymentData,
                amortizationViewMode,
                amortizationYear
            }
        };

        const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `principal-saver-config-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleLoadConfig = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const config = JSON.parse(e.target.result);

                // Basic validation
                if (!config.data || !config.data.loanData) {
                    alert('Invalid configuration file format.');
                    return;
                }

                // Restore state
                if (config.data.loanData) setLoanData(config.data.loanData);
                if (config.data.manualOverrides) setManualOverrides(config.data.manualOverrides);
                if (config.data.comparisons) setComparisons(config.data.comparisons);
                if (config.data.prepaymentData) setPrepaymentData(config.data.prepaymentData);
                if (config.data.amortizationViewMode) setAmortizationViewMode(config.data.amortizationViewMode);
                if (config.data.amortizationYear) setAmortizationYear(config.data.amortizationYear);

                // Reset file input
                if (fileInputRef.current) fileInputRef.current.value = '';

                alert('Configuration loaded successfully!');
            } catch (error) {
                console.error('Error loading configuration:', error);
                alert('Error parsing configuration file. Please ensure it is a valid JSON file.');
            }
        };
        reader.readAsText(file);
    };

    return (
        <div className="grid grid-3" style={{ alignItems: 'start' }}>
            {/* Left Column: Inputs */}
            <div style={{ gridColumn: 'span 1' }}>
                <LoanInputForm loanData={loanData} setLoanData={setLoanData} />
            </div>

            {/* Right Column: Analysis & Results */}
            <div style={{ gridColumn: 'span 2' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.25rem', marginBottom: '0.25rem' }}>
                    <button type="button" className="btn btn-icon" onClick={handleExportPDF} title="Export to PDF">
                        <FileText size={20} />
                    </button>
                    <button type="button" className="btn btn-icon" onClick={handleExportExcel} title="Export to Excel">
                        <FileSpreadsheet size={20} />
                    </button>
                    <button type="button" className="btn btn-icon" onClick={handlePrint} title="Print">
                        <Printer size={20} />
                    </button>
                    <button type="button" className="btn btn-icon" onClick={handleShare} title="Share Configuration">
                        <Share2 size={20} />
                    </button>
                    <button type="button" className="btn btn-icon" onClick={handleSaveConfig} title="Save Configuration">
                        <Save size={20} />
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleLoadConfig}
                        accept=".json"
                        style={{ display: 'none' }}
                    />
                    <button type="button" className="btn btn-icon" onClick={() => fileInputRef.current?.click()} title="Load Configuration">
                        <FolderOpen size={20} />
                    </button>
                </div>

                <div className="tabs">
                    <button
                        className={`tab ${activeTab === 'monthly' ? 'active' : ''}`}
                        onClick={() => setActiveTab('monthly')}
                    >
                        Monthly Cost
                    </button>
                    <button
                        className={`tab ${activeTab === 'amortization' ? 'active' : ''}`}
                        onClick={() => setActiveTab('amortization')}
                    >
                        Amortization
                    </button>
                    <button
                        className={`tab ${activeTab === 'prepayment' ? 'active' : ''}`}
                        onClick={() => setActiveTab('prepayment')}
                    >
                        Prepayment Calculator
                    </button>
                    <button
                        className={`tab ${activeTab === 'rates' ? 'active' : ''}`}
                        onClick={() => setActiveTab('rates')}
                    >
                        Interest Rate Range
                    </button>
                    <button
                        className={`tab ${activeTab === 'comparison' ? 'active' : ''}`}
                        onClick={() => setActiveTab('comparison')}
                    >
                        Loan Comparison
                    </button>
                </div>

                <div className="fade-in">
                    {activeTab === 'monthly' && (
                        <MonthlyCostSummary
                            loanData={loanData}
                            calculatedData={calculatedData}
                            manualOverrides={manualOverrides}
                            setManualOverrides={setManualOverrides}
                        />
                    )}

                    {activeTab === 'amortization' && (
                        <AmortizationSchedule
                            schedule={calculatedData.amortizationSchedule}
                            loanData={loanData}
                            viewMode={amortizationViewMode}
                            setViewMode={setAmortizationViewMode}
                            selectedYear={amortizationYear}
                            setSelectedYear={setAmortizationYear}
                            escrow={calculatedData.escrow}
                            pmi={calculatedData.pmi}
                        />
                    )}

                    {activeTab === 'prepayment' && (
                        <PrepaymentCalculator
                            loanData={loanData}
                            calculatedData={calculatedData}
                            prepaymentData={prepaymentData}
                            setPrepaymentData={setPrepaymentData}
                        />
                    )}

                    {activeTab === 'rates' && (
                        <InterestRateComparison
                            loanData={loanData}
                            calculatedData={calculatedData}
                        />
                    )}

                    {activeTab === 'comparison' && (
                        <LoanComparison
                            currentLoan={loanData}
                            comparisons={comparisons}
                            setComparisons={setComparisons}
                        />
                    )}
                </div>
            </div>
        </div >
    );
}

export default CalculatorPage;
